"use client";

import { useEffect, useState, useRef, useCallback } from "react";
import { TierList } from "@/types/tier-list";
import {
  getTierItemsByTierListId,
  resolveImage,
  saveTierItem,
  saveImage,
  deleteTierItem,
  saveTierList,
} from "@/lib/indexdb";
import { Input } from "@/components/ui/input";
import { Upload, Edit2, Trash2, X } from "lucide-react";
import { TierListHeader } from "@/components/tier-list-header";
import { v4 as uuidv4 } from "uuid";
import {
  PresentationModeDialog,
  PresentationModeConfig,
} from "@/components/presentation-mode-dialog";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragOverEvent,
  DragEndEvent,
  UniqueIdentifier,
  MeasuringStrategy,
  defaultDropAnimationSideEffects,
  CollisionDetection,
  pointerWithin,
  rectIntersection,
  getFirstCollision,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { DroppableContainer } from "./tier-list/DroppableContainer";
import { DiscussingArea } from "./tier-list/DiscussingArea";
import { VideoPlaceholder } from "./video-placeholder";
import { ItemPickerContextMenu } from "./item-picker-context-menu";

interface TierListEditorProps {
  tierList: TierList;
  onTierListChange: (tierListId: string) => void;
}

const UNASSIGNED = "unassigned";
const DISCUSSING = "discussing";

export function TierListEditor({
  tierList: initialTierList,
  onTierListChange,
}: TierListEditorProps) {
  const [tierList, setTierList] = useState(initialTierList);

  // State structure: Record<containerId, itemId[]>
  const [containerItems, setContainerItems] = useState<
    Record<string, string[]>
  >({});

  // Lookup tables for item metadata
  const [itemMetadata, setItemMetadata] = useState<
    Record<string, { name: string; imageId: string }>
  >({});
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [overId, setOverId] = useState<UniqueIdentifier | null>(null);

  // State for native file drag and drop
  const [isNativeFileDragging, setIsNativeFileDragging] = useState(false);
  const [nativeDragTarget, setNativeDragTarget] = useState<
    "sidebar" | "tierlist" | null
  >(null);

  // Refs for handling edge cases (from official example)
  const lastOverId = useRef<UniqueIdentifier | null>(null);
  const recentlyMovedToNewContainer = useRef(false);

  // Ref for auto-scaling (main scrolling container)
  const mainTierListAreaRef = useRef<HTMLDivElement>(null);
  // Ref to preserve current size across effect re-runs
  const currentSizeRef = useRef<number>(16);
  // Ref to store scale function so other functions can call it
  const performScaleRef = useRef<(() => void) | null>(null);
  // Consolidated scaling state ref
  const scalingStateRef = useRef({
    isScaling: false,
    rafId: null as number | null,
    lastDirection: null as "up" | "down" | null,
    lastTriggerTime: 0,
    hasMounted: false,
    sizeHistory: [] as Array<{ size: number; timestamp: number }>,
  });

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [lastSelectedItem, setLastSelectedItem] = useState<string | null>(null);

  // Presentation mode state
  const [isPresentationMode, setIsPresentationMode] = useState(false);
  const [showPresentationDialog, setShowPresentationDialog] = useState(false);
  const [presentationConfig, setPresentationConfig] =
    useState<PresentationModeConfig>({
      sidebarMode: "keep",
      recordingMode: false,
    });
  const [showVideoPlaceholder, setShowVideoPlaceholder] = useState(true);

  // Update local tierList when prop changes
  useEffect(() => {
    setTierList(initialTierList);
  }, [initialTierList]);

  // Auto-scaling logic (simplified - only triggers on specific actions)
  useEffect(() => {
    const container = mainTierListAreaRef.current;
    const state = scalingStateRef.current; // Capture for cleanup
    if (!container) {
      return;
    }

    // Initialize CSS variable with preserved size
    document.documentElement.style.setProperty(
      "--tiers-root-size",
      `${currentSizeRef.current}px`
    );

    const performScale = () => {
      console.log("[Scale] 🎬 performScale() called");

      // Debounce: Ignore if called within 200ms of last trigger
      const now = Date.now();
      if (now - state.lastTriggerTime < 200) {
        console.log("[Scale] ⏱️ Debounced - too soon");
        return;
      }
      state.lastTriggerTime = now;

      // Cancel any existing animation
      if (state.rafId !== null) {
        cancelAnimationFrame(state.rafId);
        state.rafId = null;
      }

      if (state.isScaling) {
        state.isScaling = false;
      }

      state.isScaling = true;

      const scaleStep = () => {
        // Add reflow delay: wait one extra frame before measuring to ensure DOM has updated
        requestAnimationFrame(() => {
          // Helper: Check if size was recently used (within 500ms) to prevent oscillation
          const wasRecentlyUsed = (size: number): boolean => {
            const now = Date.now();
            return state.sizeHistory.some(
              (entry) =>
                Math.abs(entry.size - size) < 0.5 && now - entry.timestamp < 500
            );
          };

          // Helper: Record size in history (keep last 3)
          const recordSize = (size: number): void => {
            state.sizeHistory.push({ size, timestamp: Date.now() });
            if (state.sizeHistory.length > 3) {
              state.sizeHistory.shift();
            }
          };

          const containerHeight = container.clientHeight;
          // Measure the actual content height (first child element)
          const contentElement = container.firstElementChild as HTMLElement;
          const contentHeight = contentElement ? contentElement.scrollHeight : container.scrollHeight;

          console.log("[Scale] 📏 Measurements:", {
            containerHeight,
            contentHeight,
            difference: contentHeight - containerHeight,
            currentSize: currentSizeRef.current,
            lastDirection: state.lastDirection,
          });

          // Dead zone with symmetric thresholds (tighter for more precision)
          const deadZone = 5;
          const hasOverflow = contentHeight > containerHeight + deadZone;
          const hasRoom = contentHeight < containerHeight - deadZone;
          const inDeadZone = !hasOverflow && !hasRoom;

          console.log("[Scale] 🎯 Conditions:", {
            hasOverflow,
            hasRoom,
            inDeadZone,
            deadZone,
          });

          // If we're in the dead zone, don't scale (this prevents oscillation)
          if (inDeadZone) {
            console.log("[Scale] ✋ In DEAD ZONE - stopping");
            state.isScaling = false;
            state.rafId = null;
            return;
          }

          let needsAnotherFrame = false;

          // Hysteresis: Prevent immediate reversal ONLY for small changes near boundaries
          // Large changes (like window resize) should allow bidirectional scaling
          const differenceFromIdeal = Math.abs(contentHeight - containerHeight);
          const isSmallChange = differenceFromIdeal < 20; // Small = oscillation risk

          const shouldPreventReversal =
            isSmallChange && (
              (state.lastDirection === "down" && hasRoom && !hasOverflow) ||
              (state.lastDirection === "up" && hasOverflow && !hasRoom)
            );

          console.log("[Scale] 🔄 Hysteresis check:", {
            differenceFromIdeal,
            isSmallChange,
            shouldPreventReversal,
          });

          if (shouldPreventReversal) {
            console.log("[Scale] 🚫 Hysteresis BLOCKED reversal");
            state.isScaling = false;
            state.rafId = null;
            return;
          }

          if (hasOverflow && currentSizeRef.current > 8) {
            console.log("[Scale] 📉 SCALING DOWN branch");

            // Calculate target size based on ratio
            const ratio = containerHeight / contentHeight;
            const targetSize = currentSizeRef.current * ratio * 0.98; // 98% safety margin
            const clampedTarget = Math.max(8, targetSize);

            // Move towards target gradually for smooth animation
            const step = Math.max(0.5, Math.abs(clampedTarget - currentSizeRef.current) * 0.3);
            const newSize = Math.max(8, currentSizeRef.current - step);

            // Early exit: Skip if size change is negligible
            if (Math.abs(newSize - currentSizeRef.current) < 0.1) {
              console.log("[Scale] ⚠️ Size change too small, stopping");
              state.isScaling = false;
              state.rafId = null;
              return;
            }

            // Check if this size was recently used to prevent oscillation
            if (wasRecentlyUsed(newSize)) {
              console.log("[Scale] ⚠️ Size was recently used, stopping");
              state.isScaling = false;
              state.rafId = null;
              return;
            }

            console.log("[Scale] ⬇️ Setting size:", currentSizeRef.current, "→", newSize);
            currentSizeRef.current = newSize;
            recordSize(newSize);
            document.documentElement.style.setProperty(
              "--tiers-root-size",
              `${currentSizeRef.current}px`
            );
            state.lastDirection = "down";

            needsAnotherFrame = Math.abs(clampedTarget - currentSizeRef.current) > 0.2;
          } else if (hasRoom && currentSizeRef.current < 16) {
            console.log("[Scale] 📈 SCALING UP branch");

            // Scale up gradually
            const newSize = Math.min(16, currentSizeRef.current + 0.5);

            // Early exit: Skip if size change is negligible
            if (Math.abs(newSize - currentSizeRef.current) < 0.1) {
              console.log("[Scale] ⚠️ Size change too small, stopping");
              state.isScaling = false;
              state.rafId = null;
              return;
            }

            // Check if this size was recently used to prevent oscillation
            if (wasRecentlyUsed(newSize)) {
              console.log("[Scale] ⚠️ Size was recently used, stopping");
              state.isScaling = false;
              state.rafId = null;
              return;
            }

            console.log("[Scale] ⬆️ Setting size:", currentSizeRef.current, "→", newSize);
            currentSizeRef.current = newSize;
            recordSize(newSize);
            document.documentElement.style.setProperty(
              "--tiers-root-size",
              `${currentSizeRef.current}px`
            );
            state.lastDirection = "up";

            needsAnotherFrame = currentSizeRef.current < 16;
          } else {
            console.log("[Scale] ⏹️ No scaling needed (at limits or other condition)");
          }

          if (needsAnotherFrame) {
            console.log("[Scale] 🔄 Scheduling next frame");
            state.rafId = requestAnimationFrame(scaleStep);
          } else {
            console.log("[Scale] ✅ Scaling complete at:", currentSizeRef.current);
            state.isScaling = false;
            state.rafId = null;
          }
        });
      };

      state.rafId = requestAnimationFrame(scaleStep);
    };

    // Store in ref so other functions can call it
    performScaleRef.current = performScale;

    // Initial scale ONLY on first mount - wait for DOM to be ready
    if (!scalingStateRef.current.hasMounted) {
      scalingStateRef.current.hasMounted = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          performScale();
        });
      });
    }

    // Window resize listener
    const handleWindowResize = () => {
      console.log("[Scale] 🪟 Window resize triggered");
      performScale();
    };
    window.addEventListener("resize", handleWindowResize);

    return () => {
      if (state.rafId !== null) {
        cancelAnimationFrame(state.rafId);
        state.rafId = null;
      }
      window.removeEventListener("resize", handleWindowResize);
      state.isScaling = false;
    };
    // Only run once on mount - scaling is triggered manually via performScaleRef
  }, []);

  // Load items from IndexedDB and structure them for dnd-kit
  const loadItems = useCallback(async () => {
    const tierItems = await getTierItemsByTierListId(tierList.id);

    // Build container-to-items mapping
    const containers: Record<string, string[]> = {
      [UNASSIGNED]: [],
      [DISCUSSING]: [],
    };

    // Initialize containers for each tier
    tierList.tiers.forEach((tier) => {
      containers[tier.id] = [];
    });

    // Build metadata lookup
    const metadata: Record<string, { name: string; imageId: string }> = {};

    // Distribute items into containers
    tierItems.forEach((item) => {
      const containerId = item.tierId || UNASSIGNED;
      if (!containers[containerId]) {
        containers[containerId] = [];
      }
      containers[containerId].push(item.id);
      metadata[item.id] = {
        name: item.name,
        imageId: item.imageId,
      };
    });

    // Sort items within each container by order
    Object.keys(containers).forEach((containerId) => {
      containers[containerId].sort((a, b) => {
        const itemA = tierItems.find((item) => item.id === a);
        const itemB = tierItems.find((item) => item.id === b);
        return (itemA?.order || 0) - (itemB?.order || 0);
      });
    });

    setContainerItems(containers);
    setItemMetadata(metadata);

    // Load image URLs
    const urls: Record<string, string> = {};
    for (const item of tierItems) {
      const url = await resolveImage(item.imageId);
      if (url) {
        urls[item.id] = url;
      }
    }
    setImageUrls(urls);
  }, [tierList.id, tierList.tiers]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Presentation mode handlers
  const handleEnterPresentationMode = useCallback(
    (config: PresentationModeConfig) => {
      setPresentationConfig(config);
      setIsPresentationMode(true);
      setShowVideoPlaceholder(true); // Show video placeholder when entering recording mode

      // Trigger auto-scaling after entering presentation mode (layout changes)
      if (performScaleRef.current) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (performScaleRef.current) {
              performScaleRef.current();
            }
          });
        });
      }
    },
    []
  );

  const handleExitPresentationMode = useCallback(async () => {
    // Move all items from discussing area back to unassigned (sidebar)
    const discussingItems = containerItems[DISCUSSING] || [];
    if (discussingItems.length > 0) {
      setContainerItems((prev) => {
        return {
          ...prev,
          [DISCUSSING]: [],
          [UNASSIGNED]: [...(prev[UNASSIGNED] || []), ...discussingItems],
        };
      });

      // Persist to IndexedDB for each item moved back
      try {
        for (let i = 0; i < discussingItems.length; i++) {
          const itemId = discussingItems[i];
          const metadata = itemMetadata[itemId];
          if (metadata) {
            await saveTierItem({
              id: itemId,
              name: metadata.name,
              imageId: metadata.imageId,
              tierListId: tierList.id,
              tierId: undefined, // Move to unassigned
              order: (containerItems[UNASSIGNED]?.length || 0) + i,
            });
          }
        }
      } catch (error) {
        console.error(
          "Failed to persist discussing items back to sidebar:",
          error
        );
      }
    }

    setIsPresentationMode(false);
    // Reset config to defaults (session-only)
    setPresentationConfig({
      sidebarMode: "keep",
      recordingMode: false,
    });
    setShowVideoPlaceholder(true); // Reset for next time

    // Trigger auto-scaling after exiting presentation mode (layout changes)
    if (performScaleRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (performScaleRef.current) {
            performScaleRef.current();
          }
        });
      });
    }
  }, [containerItems, itemMetadata, tierList.id]);

  // Global keyboard listener for presentation mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+P (Mac) or Ctrl+P (Windows) to toggle presentation mode
      if ((e.metaKey || e.ctrlKey) && e.key === "p") {
        e.preventDefault();
        if (isPresentationMode) {
          // Exit presentation mode
          handleExitPresentationMode();
        } else {
          // Show dialog to configure and enter presentation mode
          setShowPresentationDialog(true);
        }
      }
      // ESC to exit presentation mode
      else if (e.key === "Escape" && isPresentationMode) {
        e.preventDefault();
        handleExitPresentationMode();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isPresentationMode, handleExitPresentationMode]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Helper to find which container an item belongs to
  const findContainer = (id: UniqueIdentifier): string | undefined => {
    if (id in containerItems) {
      return id as string;
    }

    // Handle dropzone IDs (e.g., "discussing-dropzone" -> "discussing")
    const idStr = id as string;
    if (idStr.endsWith("-dropzone")) {
      const containerId = idStr.replace("-dropzone", "");
      if (containerId in containerItems) {
        return containerId;
      }
    }

    return Object.keys(containerItems).find((key) =>
      containerItems[key].includes(id as string)
    );
  };

  // Custom collision detection strategy (from official dnd-kit example)
  const collisionDetectionStrategy: CollisionDetection = useCallback(
    (args) => {
      if (activeId && activeId in containerItems) {
        // If dragging a container, only detect container collisions
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter(
            (container) => container.id in containerItems
          ),
        });
      }

      // Start by finding any intersecting droppable
      const pointerIntersections = pointerWithin(args);
      const intersections =
        pointerIntersections.length > 0
          ? pointerIntersections
          : rectIntersection(args);
      let overId = getFirstCollision(intersections, "id");

      if (overId != null) {
        if (overId in containerItems) {
          const containerItemsList = containerItems[overId];

          // If a container is matched and it contains items
          if (containerItemsList.length > 0) {
            // Return the closest droppable within that container
            overId = closestCenter({
              ...args,
              droppableContainers: args.droppableContainers.filter(
                (container) =>
                  container.id !== overId &&
                  containerItemsList.includes(container.id as string)
              ),
            })[0]?.id;
          }
        }

        lastOverId.current = overId;
        return [{ id: overId }];
      }

      // When a draggable item moves to a new container, the layout may shift
      // and the `overId` may become `null`. We use the cached `lastOverId`
      if (recentlyMovedToNewContainer.current && lastOverId.current != null) {
        return [{ id: lastOverId.current }];
      }

      return lastOverId.current != null ? [{ id: lastOverId.current }] : [];
    },
    [activeId, containerItems]
  );

  // Helper function to process files (used by both upload and drop)
  const processFiles = async (files: File[]) => {
    if (files.length === 0) return;

    const newItemIds: string[] = [];
    const newMetadata: Record<string, { name: string; imageId: string }> = {};
    const newImageUrls: Record<string, string> = {};

    // First, prepare all items and save images
    const itemsToSave: Array<{
      id: string;
      name: string;
      imageId: string;
    }> = [];

    for (const file of files) {
      const imageId = uuidv4();
      const itemId = uuidv4();
      const name = file.name.replace(/\.[^/.]+$/, "");

      // Save image to IndexedDB
      await saveImage({
        id: imageId,
        tierListId: tierList.id,
        blob: file,
        mimeType: file.type,
      });

      itemsToSave.push({ id: itemId, name, imageId });
      newItemIds.push(itemId);
      newMetadata[itemId] = { name, imageId };

      // Create blob URL
      const url = URL.createObjectURL(file);
      newImageUrls[itemId] = url;
    }

    // Use functional setState to get current length atomically
    let startOrder = 0;
    setContainerItems((prev) => {
      startOrder = prev[UNASSIGNED]?.length || 0;
      return {
        ...prev,
        [UNASSIGNED]: [...(prev[UNASSIGNED] || []), ...newItemIds],
      };
    });

    // Save tier items with correct order values
    for (let i = 0; i < itemsToSave.length; i++) {
      const item = itemsToSave[i];
      await saveTierItem({
        id: item.id,
        name: item.name,
        imageId: item.imageId,
        tierListId: tierList.id,
        tierId: undefined,
        order: startOrder + i,
      });
    }

    setItemMetadata((prev) => ({ ...prev, ...newMetadata }));
    setImageUrls((prev) => ({ ...prev, ...newImageUrls }));

    // Trigger auto-scaling after adding items to unassigned
    if (performScaleRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (performScaleRef.current) {
            performScaleRef.current();
          }
        });
      });
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    await processFiles(Array.from(files));
    e.target.value = "";
  };

  // Move item back to sidebar (from tier)
  const handleRemoveItem = async (itemId: string) => {
    const metadata = itemMetadata[itemId];
    if (!metadata) return;

    // Find which container the item is currently in
    const currentContainer = Object.keys(containerItems).find((containerId) =>
      containerItems[containerId].includes(itemId)
    );

    // Only proceed if item is in a tier (not already in unassigned)
    if (!currentContainer || currentContainer === UNASSIGNED) return;

    // Move item to unassigned
    setContainerItems((prev) => {
      const newContainers = { ...prev };
      // Remove from current container
      newContainers[currentContainer] = newContainers[currentContainer].filter(
        (id) => id !== itemId
      );
      // Add to unassigned at the end
      newContainers[UNASSIGNED] = [...newContainers[UNASSIGNED], itemId];
      return newContainers;
    });

    // Update in IndexedDB
    try {
      await saveTierItem({
        id: itemId,
        name: metadata.name,
        imageId: metadata.imageId,
        tierListId: tierList.id,
        tierId: undefined, // Set to unassigned
        order: containerItems[UNASSIGNED]?.length || 0,
      });
    } catch (error) {
      console.error("Failed to move item back to sidebar:", error);
      // Reload items from DB to ensure UI matches persisted state
      await loadItems();
    }

    // Trigger auto-scaling after removing item from tier
    // Wait for DOM to update before measuring
    if (performScaleRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (performScaleRef.current) {
            performScaleRef.current();
          }
        });
      });
    }
  };

  // Permanently delete item
  const handleDeleteItem = async (itemId: string) => {
    await deleteTierItem(itemId);

    // Remove from container
    setContainerItems((prev) => {
      const newContainers = { ...prev };
      Object.keys(newContainers).forEach((containerId) => {
        newContainers[containerId] = newContainers[containerId].filter(
          (id) => id !== itemId
        );
      });
      return newContainers;
    });

    // Clean up metadata
    setItemMetadata((prev) => {
      const newMetadata = { ...prev };
      delete newMetadata[itemId];
      return newMetadata;
    });

    // Clean up image URL
    const metadata = itemMetadata[itemId];
    if (metadata && imageUrls[itemId]) {
      URL.revokeObjectURL(imageUrls[itemId]);
      setImageUrls((prev) => {
        const newUrls = { ...prev };
        delete newUrls[itemId];
        return newUrls;
      });
    }

    // Trigger auto-scaling after deleting item (frees space)
    if (performScaleRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (performScaleRef.current) {
            performScaleRef.current();
          }
        });
      });
    }
  };

  // Move item from unassigned to a target container (for context menu)
  const handleMoveItem = async (itemId: string, targetContainerId: string) => {
    const sourceContainerId = findContainer(itemId);
    if (!sourceContainerId) return;

    // Update local state
    setContainerItems((prev) => {
      const newContainers = { ...prev };

      // Remove from source
      newContainers[sourceContainerId] = newContainers[
        sourceContainerId
      ].filter((id) => id !== itemId);

      // Add to target at the end
      if (!newContainers[targetContainerId]) {
        newContainers[targetContainerId] = [];
      }
      newContainers[targetContainerId] = [
        ...newContainers[targetContainerId],
        itemId,
      ];

      return newContainers;
    });

    // Update in IndexedDB
    try {
      const metadata = itemMetadata[itemId];
      if (metadata) {
        await saveTierItem({
          id: itemId,
          name: metadata.name,
          imageId: metadata.imageId,
          tierListId: tierList.id,
          tierId:
            targetContainerId === UNASSIGNED || targetContainerId === DISCUSSING
              ? undefined
              : targetContainerId,
          order: containerItems[targetContainerId]?.length || 0,
        });
      }
    } catch (error) {
      console.error("Failed to move item:", error);
      // Reload items from DB to ensure UI matches persisted state
      await loadItems();
    }

    // Trigger auto-scaling after moving item between containers
    if (performScaleRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (performScaleRef.current) {
            performScaleRef.current();
          }
        });
      });
    }
  };

  const handleTierLabelChange = async (tierId: string, newLabel: string) => {
    const updatedTiers = tierList.tiers.map((tier) =>
      tier.id === tierId ? { ...tier, label: newLabel } : tier
    );

    const updatedTierList = {
      ...tierList,
      tiers: updatedTiers,
    };

    await saveTierList(updatedTierList);
    setTierList(updatedTierList);
  };

  const handleTierFontSizeChange = async (
    tierId: string,
    multiplier: number
  ) => {
    const updatedTiers = tierList.tiers.map((tier) =>
      tier.id === tierId ? { ...tier, fontSizeMultiplier: multiplier } : tier
    );

    const updatedTierList = {
      ...tierList,
      tiers: updatedTiers,
    };

    await saveTierList(updatedTierList);
    setTierList(updatedTierList);

    // Trigger auto-scaling after changing tier font size (affects all tier content)
    if (performScaleRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (performScaleRef.current) {
            performScaleRef.current();
          }
        });
      });
    }
  };

  // Edit mode handlers
  const handleEnterEditMode = () => {
    setIsEditMode(true);
    setSelectedItems(new Set());
    setLastSelectedItem(null);
  };

  const handleExitEditMode = () => {
    setIsEditMode(false);
    setSelectedItems(new Set());
    setLastSelectedItem(null);
  };

  const handleToggleItemSelection = (itemId: string, isShiftClick: boolean) => {
    if (isShiftClick && lastSelectedItem) {
      // Shift-click: select/deselect range
      const unassignedItems = containerItems[UNASSIGNED] || [];
      const lastIndex = unassignedItems.indexOf(lastSelectedItem);
      const currentIndex = unassignedItems.indexOf(itemId);

      if (lastIndex !== -1 && currentIndex !== -1) {
        const start = Math.min(lastIndex, currentIndex);
        const end = Math.max(lastIndex, currentIndex);
        const rangeItems = unassignedItems.slice(start, end + 1);

        // Determine if we're selecting or deselecting based on the clicked item
        const isCurrentlySelected = selectedItems.has(itemId);

        setSelectedItems((prev) => {
          const newSelected = new Set(prev);
          rangeItems.forEach((id) => {
            if (isCurrentlySelected) {
              newSelected.delete(id);
            } else {
              newSelected.add(id);
            }
          });
          return newSelected;
        });
      }
    } else {
      // Regular click: toggle single item
      setSelectedItems((prev) => {
        const newSelected = new Set(prev);
        if (newSelected.has(itemId)) {
          newSelected.delete(itemId);
        } else {
          newSelected.add(itemId);
        }
        return newSelected;
      });
      setLastSelectedItem(itemId);
    }
  };

  const handleDeleteSelectedItems = async () => {
    const itemsToDelete = Array.from(selectedItems);

    try {
      // Delete all items in parallel for better performance
      await Promise.all(
        itemsToDelete.map((itemId) => handleDeleteItem(itemId))
      );

      // Clear selection and exit edit mode
      handleExitEditMode();
    } catch (error) {
      console.error("Failed to delete items:", error);
      // Reload items from DB to ensure UI matches persisted state
      await loadItems();
      // Exit edit mode even if deletion failed
      handleExitEditMode();
    }

    // Trigger auto-scaling after batch delete (frees significant space)
    if (performScaleRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (performScaleRef.current) {
            performScaleRef.current();
          }
        });
      });
    }
  };

  // Native file drag and drop handlers
  const handleNativeDragEnter = (
    e: React.DragEvent,
    target: "sidebar" | "tierlist"
  ) => {
    e.preventDefault();
    e.stopPropagation();

    // Disable native file drag in edit mode
    if (isEditMode) return;

    // Check if the drag contains files
    if (e.dataTransfer.types.includes("Files")) {
      setIsNativeFileDragging(true);
      setNativeDragTarget(target);
    }
  };

  const handleNativeDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Set the dropEffect to indicate this is a copy operation
    if (e.dataTransfer.types.includes("Files")) {
      e.dataTransfer.dropEffect = "copy";
    }
  };

  const handleNativeDragLeave = (
    e: React.DragEvent,
    target: "sidebar" | "tierlist"
  ) => {
    e.preventDefault();
    e.stopPropagation();

    // Only reset if we're leaving the target area (not entering a child)
    if (e.currentTarget === e.target) {
      if (nativeDragTarget === target) {
        setIsNativeFileDragging(false);
        setNativeDragTarget(null);
      }
    }
  };

  const handleNativeDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setIsNativeFileDragging(false);
    setNativeDragTarget(null);

    // Disable native file drop in edit mode
    if (isEditMode) return;

    // Get files from the drop event
    const files = Array.from(e.dataTransfer.files).filter((file) =>
      file.type.startsWith("image/")
    );

    if (files.length > 0) {
      await processFiles(files);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    const currentOverId = over?.id;

    setOverId(currentOverId || null);

    if (!currentOverId) {
      return;
    }

    const activeContainer = findContainer(active.id);
    const overContainer = findContainer(currentOverId);

    if (
      !activeContainer ||
      !overContainer ||
      activeContainer === overContainer
    ) {
      return;
    }

    // Prevent adding to discussing area if it already has an item
    if (
      overContainer === DISCUSSING &&
      containerItems[DISCUSSING].length > 0 &&
      !containerItems[DISCUSSING].includes(active.id as string)
    ) {
      // Discussing area is full and we're trying to add a different item
      return;
    }

    // Move item from one container to another
    setContainerItems((prev) => {
      const overIndex = prev[overContainer].indexOf(currentOverId as string);

      let newIndex: number;
      if (currentOverId in prev) {
        // Dropped on a container - add to end
        newIndex = prev[overContainer].length;
      } else {
        // Dropped on an item - insert before/after
        const isBelowOverItem =
          over &&
          active.rect.current.translated &&
          active.rect.current.translated.top > over.rect.top + over.rect.height;
        const modifier = isBelowOverItem ? 1 : 0;
        newIndex =
          overIndex >= 0 ? overIndex + modifier : prev[overContainer].length;
      }

      return {
        ...prev,
        [activeContainer]: prev[activeContainer].filter(
          (id) => id !== active.id
        ),
        [overContainer]: [
          ...prev[overContainer].slice(0, newIndex),
          active.id as string,
          ...prev[overContainer].slice(newIndex),
        ],
      };
    });

    lastOverId.current = currentOverId;
    recentlyMovedToNewContainer.current = true;
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveId(null);
    setOverId(null);

    if (!over) {
      return;
    }

    const activeContainer = findContainer(active.id);

    if (!activeContainer) {
      return;
    }

    const currentOverId = over.id;
    const overContainer = findContainer(currentOverId);

    if (!overContainer) {
      return;
    }

    // If dragging within the same container, reorder
    if (activeContainer === overContainer) {
      const activeIndex = containerItems[activeContainer].indexOf(
        active.id as string
      );
      const overIndex = containerItems[overContainer].indexOf(
        currentOverId as string
      );

      if (activeIndex !== overIndex) {
        setContainerItems((items) => ({
          ...items,
          [overContainer]: arrayMove(
            items[overContainer],
            activeIndex,
            overIndex
          ),
        }));
      }
    }

    // Persist all items in affected containers to IndexedDB
    const containersToUpdate = new Set([activeContainer, overContainer]);

    try {
      for (const containerId of containersToUpdate) {
        const itemsInContainer = containerItems[containerId];

        for (let i = 0; i < itemsInContainer.length; i++) {
          const itemId = itemsInContainer[i];
          const metadata = itemMetadata[itemId];

          if (metadata) {
            await saveTierItem({
              id: itemId,
              name: metadata.name,
              imageId: metadata.imageId,
              tierListId: tierList.id,
              tierId: containerId === UNASSIGNED ? undefined : containerId,
              order: i,
            });
          }
        }
      }
    } catch (error) {
      console.error("Failed to persist drag operation to IndexedDB:", error);
      // Reload items from DB to ensure UI matches persisted state
      await loadItems();
    }

    // Trigger auto-scaling after drag ends
    // Wait for DOM to update before measuring
    if (performScaleRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (performScaleRef.current) {
            performScaleRef.current();
          }
        });
      });
    }

    recentlyMovedToNewContainer.current = false;
  };

  const sortedTiers = [...tierList.tiers].sort(
    (a, b) => a.priority - b.priority
  );

  const activeItem =
    activeId && itemMetadata[activeId as string]
      ? {
          id: activeId as string,
          ...itemMetadata[activeId as string],
        }
      : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetectionStrategy}
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.Always,
        },
      }}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="flex h-screen bg-background">
        {/* Sidebar - hidden in presentation mode with sidebarless option */}
        {!(
          isPresentationMode && presentationConfig.sidebarMode === "sidebarless"
        ) && (
          <div className="w-64 bg-card border-r border-border flex flex-col">
            {!isPresentationMode && (
              <div className="p-4 border-b border-border">
                <h2 className="text-lg font-semibold text-white mb-4">
                  Available Items
                </h2>
                {!isEditMode ? (
                  <div className="flex gap-2">
                    <label className="flex-1">
                      <div className="flex items-center justify-center gap-2 px-4 py-2 bg-secondary hover:bg-secondary/80 rounded-md cursor-pointer transition-colors">
                        <Upload className="w-4 h-4" />
                        <span className="text-sm">Upload</span>
                      </div>
                      <Input
                        type="file"
                        multiple
                        accept="image/*"
                        className="hidden"
                        onChange={handleFileUpload}
                        disabled={isEditMode}
                      />
                    </label>
                    <button
                      onClick={handleEnterEditMode}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-secondary hover:bg-secondary/80 rounded-md cursor-pointer transition-colors"
                      title="Edit mode"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={handleDeleteSelectedItems}
                      disabled={selectedItems.size === 0}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-destructive hover:bg-destructive/80 disabled:bg-destructive/50 disabled:cursor-not-allowed rounded-md cursor-pointer transition-colors"
                      title="Delete selected"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="text-sm">
                        Delete{" "}
                        {selectedItems.size > 0
                          ? `(${selectedItems.size})`
                          : ""}
                      </span>
                    </button>
                    <button
                      onClick={handleExitEditMode}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-secondary hover:bg-secondary/80 rounded-md cursor-pointer transition-colors"
                      title="Cancel"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            )}

            <div
              className="flex-1 overflow-y-auto rounded-lg"
              onDragEnter={(e) => handleNativeDragEnter(e, "sidebar")}
              onDragOver={handleNativeDragOver}
              onDragLeave={(e) => handleNativeDragLeave(e, "sidebar")}
              onDrop={handleNativeDrop}
              style={{
                transition: "border 0.2s, background-color 0.2s",
                border:
                  activeId &&
                  (overId === UNASSIGNED ||
                    (overId != null &&
                      (containerItems[UNASSIGNED] || []).includes(
                        overId as string
                      )))
                    ? "2px solid rgb(96, 165, 250)"
                    : isNativeFileDragging && nativeDragTarget === "sidebar"
                    ? "2px solid rgb(34, 197, 94)"
                    : "2px solid transparent",
                backgroundColor:
                  activeId &&
                  (overId === UNASSIGNED ||
                    (overId != null &&
                      (containerItems[UNASSIGNED] || []).includes(
                        overId as string
                      )))
                    ? "rgba(96, 165, 250, 0.1)"
                    : isNativeFileDragging && nativeDragTarget === "sidebar"
                    ? "rgba(34, 197, 94, 0.1)"
                    : "transparent",
              }}
            >
              <DroppableContainer
                id={UNASSIGNED}
                items={containerItems[UNASSIGNED] || []}
                imageUrls={imageUrls}
                itemNames={Object.fromEntries(
                  Object.entries(itemMetadata).map(([id, data]) => [
                    id,
                    data.name,
                  ])
                )}
                onDeleteItem={handleDeleteItem}
                deleteIcon="delete"
                deleteLabel="Delete item"
                activeId={activeId}
                overId={overId}
                isEditMode={isEditMode}
                selectedItems={selectedItems}
                onToggleItemSelection={handleToggleItemSelection}
                isPresentationMode={isPresentationMode}
              />
            </div>
          </div>
        )}

        {/* Main content area with header */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <TierListHeader
            tierList={tierList}
            onTierListChange={onTierListChange}
            isPresentationMode={isPresentationMode}
            onTogglePresentationMode={() => {
              if (isPresentationMode) {
                handleExitPresentationMode();
              } else {
                setShowPresentationDialog(true);
              }
            }}
          />

          {/* Main Tier List Area */}
          <div
            ref={mainTierListAreaRef}
            className="flex-1 overflow-y-auto"
            data-testid="main-tier-list-area"
            style={{
              ...(isPresentationMode && presentationConfig.recordingMode
                ? { display: "grid", gridTemplateColumns: "1fr auto" }
                : {}),
            }}
          >
            {/* Recording mode: Two-column layout */}
            {isPresentationMode && presentationConfig.recordingMode ? (
              <>
                {/* Left: Tier Lists */}
                <div
                  className="overflow-y-auto"
                  data-testid="tiers-list-container"
                  onDragEnter={(e) => handleNativeDragEnter(e, "tierlist")}
                  onDragOver={handleNativeDragOver}
                  onDragLeave={(e) => handleNativeDragLeave(e, "tierlist")}
                  onDrop={handleNativeDrop}
                  style={{
                    padding: "calc(var(--tiers-root-size) * 1.5)",
                    transition: "background-color 0.2s",
                    backgroundColor:
                      isNativeFileDragging && nativeDragTarget === "tierlist"
                        ? "rgba(34, 197, 94, 0.05)"
                        : "transparent",
                  }}
                >
                  <div className="" data-testid="tiers-list">
                    <div
                      className="grid"
                      style={{
                        gap: "calc(var(--tiers-root-size) * 0.5) 0",
                        gridTemplateColumns:
                          "minmax(calc(var(--tiers-root-size) * 6.25), calc(var(--tiers-root-size) * 11.25)) 1fr",
                      }}
                    >
                      {sortedTiers.map((tier) => {
                        const tierContainer = (
                          <DroppableContainer
                            key={tier.id}
                            id={tier.id}
                            items={containerItems[tier.id] || []}
                            imageUrls={imageUrls}
                            itemNames={Object.fromEntries(
                              Object.entries(itemMetadata).map(([id, data]) => [
                                id,
                                data.name,
                              ])
                            )}
                            onDeleteItem={handleRemoveItem}
                            tier={{
                              label: tier.label,
                              color: tier.color,
                              fontSizeMultiplier: tier.fontSizeMultiplier,
                            }}
                            onLabelChange={(newLabel) =>
                              handleTierLabelChange(tier.id, newLabel)
                            }
                            onFontSizeChange={(multiplier) =>
                              handleTierFontSizeChange(tier.id, multiplier)
                            }
                            deleteIcon="remove"
                            deleteLabel="Move back to sidebar"
                            activeId={activeId}
                            overId={overId}
                            isPresentationMode={isPresentationMode}
                          />
                        );

                        // Wrap with context menu if in sidebarless mode
                        if (presentationConfig.sidebarMode === "sidebarless") {
                          return (
                            <ItemPickerContextMenu
                              key={tier.id}
                              items={containerItems[UNASSIGNED] || []}
                              itemNames={Object.fromEntries(
                                Object.entries(itemMetadata).map(
                                  ([id, data]) => [id, data.name]
                                )
                              )}
                              imageUrls={imageUrls}
                              onItemSelect={(itemId) =>
                                handleMoveItem(itemId, tier.id)
                              }
                              enabled={true}
                              activeId={activeId}
                            >
                              {tierContainer}
                            </ItemPickerContextMenu>
                          );
                        }

                        return tierContainer;
                      })}
                    </div>
                  </div>
                </div>

                {/* Right: Discussing Area + Video Placeholder */}
                <div
                  style={{
                    padding: "calc(var(--tiers-root-size) * 1.5)",
                    gap: "calc(var(--tiers-root-size) * 1.5)",
                  }}
                  className="border-l border-border overflow-y-auto flex flex-col justify-between items-end"
                >
                  {/* Discussing Area */}
                  <ItemPickerContextMenu
                    items={containerItems[UNASSIGNED] || []}
                    itemNames={Object.fromEntries(
                      Object.entries(itemMetadata).map(([id, data]) => [
                        id,
                        data.name,
                      ])
                    )}
                    imageUrls={imageUrls}
                    onItemSelect={(itemId) =>
                      handleMoveItem(itemId, DISCUSSING)
                    }
                    enabled={presentationConfig.sidebarMode === "sidebarless"}
                    activeId={activeId}
                  >
                    <DiscussingArea
                      color={tierList.tiers[0].color}
                      id={DISCUSSING}
                      items={containerItems[DISCUSSING] || []}
                      imageUrls={imageUrls}
                      itemNames={Object.fromEntries(
                        Object.entries(itemMetadata).map(([id, data]) => [
                          id,
                          data.name,
                        ])
                      )}
                      onDeleteItem={handleRemoveItem}
                      activeId={activeId}
                      overId={overId}
                    />
                  </ItemPickerContextMenu>

                  {/* Video Placeholder */}
                  {showVideoPlaceholder && (
                    <VideoPlaceholder
                      onClose={() => setShowVideoPlaceholder(false)}
                    />
                  )}
                </div>
              </>
            ) : (
              /* Normal mode: Single column */
              <div
                data-testid="tiers-list-container"
                className="flex-1 overflow-y-auto"
                onDragEnter={(e) => handleNativeDragEnter(e, "tierlist")}
                onDragOver={handleNativeDragOver}
                onDragLeave={(e) => handleNativeDragLeave(e, "tierlist")}
                onDrop={handleNativeDrop}
                style={{
                  padding: "calc(var(--tiers-root-size) * 1.5)",
                  transition: "background-color 0.2s",
                  backgroundColor:
                    isNativeFileDragging && nativeDragTarget === "tierlist"
                      ? "rgba(34, 197, 94, 0.05)"
                      : "transparent",
                }}
              >
                <div className="" data-testid="tiers-list">
                  <div
                    className="grid"
                    style={{
                      gap: "calc(var(--tiers-root-size) * 0.5) 0",
                      gridTemplateColumns:
                        "minmax(calc(var(--tiers-root-size) * 6.25), calc(var(--tiers-root-size) * 11.25)) 1fr",
                    }}
                  >
                    {sortedTiers.map((tier) => {
                      const tierContainer = (
                        <DroppableContainer
                          key={tier.id}
                          id={tier.id}
                          items={containerItems[tier.id] || []}
                          imageUrls={imageUrls}
                          itemNames={Object.fromEntries(
                            Object.entries(itemMetadata).map(([id, data]) => [
                              id,
                              data.name,
                            ])
                          )}
                          onDeleteItem={handleRemoveItem}
                          tier={{
                            label: tier.label,
                            color: tier.color,
                            fontSizeMultiplier: tier.fontSizeMultiplier,
                          }}
                          onLabelChange={(newLabel) =>
                            handleTierLabelChange(tier.id, newLabel)
                          }
                          onFontSizeChange={(multiplier) =>
                            handleTierFontSizeChange(tier.id, multiplier)
                          }
                          deleteIcon="remove"
                          deleteLabel="Move back to sidebar"
                          activeId={activeId}
                          overId={overId}
                          isPresentationMode={isPresentationMode}
                        />
                      );

                      // Wrap with context menu if in presentation mode with sidebarless
                      if (
                        isPresentationMode &&
                        presentationConfig.sidebarMode === "sidebarless"
                      ) {
                        return (
                          <ItemPickerContextMenu
                            key={tier.id}
                            items={containerItems[UNASSIGNED] || []}
                            itemNames={Object.fromEntries(
                              Object.entries(itemMetadata).map(([id, data]) => [
                                id,
                                data.name,
                              ])
                            )}
                            imageUrls={imageUrls}
                            onItemSelect={(itemId) =>
                              handleMoveItem(itemId, tier.id)
                            }
                            enabled={true}
                            activeId={activeId}
                          >
                            {tierContainer}
                          </ItemPickerContextMenu>
                        );
                      }

                      return tierContainer;
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <DragOverlay
        dropAnimation={{
          sideEffects: defaultDropAnimationSideEffects({
            styles: {
              active: {
                opacity: "0.5",
              },
            },
          }),
        }}
      >
        {activeItem && (
          <div
            style={{
              width: "calc(var(--tiers-root-size) * 5)",
              height: "calc(var(--tiers-root-size) * 5)",
            }}
            className="rounded overflow-hidden bg-card border-2 border-ring shadow-lg"
          >
            {imageUrls[activeItem.id] && (
              <img
                src={imageUrls[activeItem.id]}
                alt={activeItem.name}
                className="w-full h-full object-cover"
              />
            )}
          </div>
        )}
      </DragOverlay>

      {/* Presentation Mode Configuration Dialog */}
      <PresentationModeDialog
        open={showPresentationDialog}
        onOpenChange={setShowPresentationDialog}
        onConfirm={handleEnterPresentationMode}
      />
    </DndContext>
  );
}
